// ActivePatientsTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import ActivepatientApi from "../APIs/activePatientApi";
import MedicalRecordModal from "./MedicalRecordModal";
import PrescriptionModal from "./PrescriptionModal";
import MessagesPanel from "./MessagesPanel";

export default function ActivePatientsTab({ providerId }) {
  const [page, setPage] = useState(0);
  const [size] = useState(20);

  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState(null);
  const [cardsPage, setCardsPage] = useState({
    content: [],
    totalPages: 0,
    number: 0,
  });

  const [selectedPatientId, setSelectedPatientId] = useState(null);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [insuranceDocs, setInsuranceDocs] = useState([]);
  const [viewingDoc, setViewingDoc] = useState({});

  // Modal state
  const [mrOpen, setMrOpen] = useState(false);
  const [mrMode, setMrMode] = useState("create"); // 'create' | 'edit'
  const [mrInitial, setMrInitial] = useState(null);

  const [rxOpen, setRxOpen] = useState(false);
  const [rxMode, setRxMode] = useState("create"); // 'create' | 'edit'
  const [rxInitial, setRxInitial] = useState(null);

  // Load Active Patient cards
  useEffect(() => {
    const ac = new AbortController();
    setCardsLoading(true);
    setCardsError(null);
    ActivepatientApi.getActivePatients(providerId, { page, size })
      .then((res) => setCardsPage(res))
      .catch((err) => {
        if (err.name !== "AbortError") setCardsError(err);
      })
      .finally(() => setCardsLoading(false));
    return () => ac.abort();
  }, [providerId, page, size]);

  // Load selected Active Patient detail (aggregated)
  useEffect(() => {
    if (!selectedPatientId) return;
    const ac = new AbortController();
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    setInsuranceDocs([]);
    Promise.all([
      ActivepatientApi.getActivePatientDetail(providerId, selectedPatientId),
      ActivepatientApi.getActivePatientInsuranceDocuments(
        providerId,
        selectedPatientId,
      ),
    ])
      .then(([res, docs]) => {
        setDetail(res);
        setInsuranceDocs(Array.isArray(docs) ? docs : []);
      })
      .catch((err) => {
        if (err.name !== "AbortError") setDetailError(err);
      })
      .finally(() => setDetailLoading(false));
    return () => ac.abort();
  }, [providerId, selectedPatientId]);

  const cards = useMemo(() => cardsPage?.content ?? [], [cardsPage]);

  const handleBackToList = () => {
    setSelectedPatientId(null);
    setDetail(null);
    setDetailError(null);
  };

  // After save from any modal, refresh the aggregated detail
  const refreshDetail = async () => {
    if (!selectedPatientId) return;
    const refreshed = await ActivepatientApi.getActivePatientDetail(
      providerId,
      selectedPatientId,
    );
    const docs = await ActivepatientApi.getActivePatientInsuranceDocuments(
      providerId,
      selectedPatientId,
    );
    setDetail(refreshed);
    setInsuranceDocs(Array.isArray(docs) ? docs : []);
  };

  const handleViewDocument = async (doc) => {
    setViewingDoc((v) => ({ ...v, [doc.documentId]: true }));
    try {
      const url = await ActivepatientApi.viewDocumentInline(doc.documentId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          "Unable to load document. Please try again.",
      );
    } finally {
      setViewingDoc((v) => ({ ...v, [doc.documentId]: false }));
    }
  };

  // ----- Open handlers for modals -----
  const handleAddMedicalRecord = () => {
    setMrMode("create");
    setMrInitial(null);
    setMrOpen(true);
  };

  const handleEditMedicalRecord = (recordId) => {
    const rec = detail?.medicalRecords?.find(
      (r) => (r.recordId ?? r.id) === recordId,
    );
    setMrMode("edit");
    setMrInitial(rec || { recordId, patientId: selectedPatientId, providerId });
    setMrOpen(true);
  };

  const handleAddPrescription = () => {
    setRxMode("create");
    setRxInitial(null);
    setRxOpen(true);
  };

  const handleEditPrescription = (prescriptionId) => {
    const rx = detail?.prescriptions?.find(
      (r) => (r.prescriptionId ?? r.id) === prescriptionId,
    );
    setRxMode("edit");
    setRxInitial(
      rx || { prescriptionId, patientId: selectedPatientId, providerId },
    );
    setRxOpen(true);
  };

  function fmtTime(zdtString) {
    const isoWithOffset = String(zdtString).split("[")[0];
    const d = new Date(isoWithOffset);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  // Delete handlers use your API and then refresh the detail view
  const handleDeleteMedicalRecord = async (recordId) => {
    if (!window.confirm("Delete this medical record?")) return;
    try {
      await ActivepatientApi.deleteMedicalRecord(recordId);
      await refreshDetail();
    } catch (e) {
      alert(e.message || "Failed to delete medical record");
    }
  };

  const handleDeletePrescription = async (prescriptionId) => {
    if (!window.confirm("Delete this prescription?")) return;
    try {
      await ActivepatientApi.deletePrescription(prescriptionId);
      await refreshDetail();
    } catch (e) {
      alert(e.message || "Failed to delete prescription");
    }
  };

  // ---------- Render ----------
  return (
    <>
      {selectedPatientId ? (
        // Detail view (Aggregated: profile + medicalRecords + prescriptions)
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold">Active Patient</h3>
            <button
              onClick={handleBackToList}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            >
              ← Back to Patients
            </button>
          </div>

          {detailLoading && (
            <div className="p-4 rounded border bg-white shadow-sm animate-pulse">
              Loading patient detail…
            </div>
          )}
          {detailError && (
            <div className="p-4 rounded border bg-red-50 text-red-700">
              Error: {detailError.message}
            </div>
          )}
          {detail && (
            <>
              {/* Patient Profile (view-only) */}
              <section className="rounded-2xl border bg-white p-4 shadow-sm">
                <h4 className="text-lg font-semibold mb-3">Patient Profile</h4>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <Field
                    label="First Name"
                    value={detail.patientProfile.firstName}
                  />
                  <Field
                    label="Last Name"
                    value={detail.patientProfile.lastName}
                  />
                  <Field label="Email" value={detail.patientProfile.email} />
                  <Field
                    label="Date of Birth"
                    value={detail.patientProfile.dateOfBirth}
                  />
                  <Field
                    label="Phone Number"
                    value={detail.patientProfile.phoneNumber}
                  />
                </div>
              </section>

              {/* Insurance Cards */}
              <section className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Insurance Cards</h4>
                </div>
                {insuranceDocs.length === 0 ? (
                  <EmptyState msg="No insurance cards uploaded yet." />
                ) : (
                  <ul className="divide-y">
                    {insuranceDocs.map((doc) => (
                      <li
                        key={doc.documentId}
                        className="py-3 flex items-center justify-between gap-3"
                      >
                        <div className="text-sm min-w-0">
                          <div className="font-medium truncate">
                            {doc.fileName || "Insurance card"}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {formatInsuranceDocCategory(doc.category)}
                            {doc.uploadTime
                              ? ` • Uploaded ${formatDate(doc.uploadTime)}`
                              : ""}
                          </div>
                        </div>
                        <button
                          onClick={() => handleViewDocument(doc)}
                          disabled={viewingDoc[doc.documentId]}
                          className="shrink-0 px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                          {viewingDoc[doc.documentId] ? "Opening..." : "View"}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Medical Records */}
              <section className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Medical Records</h4>
                  <button
                    onClick={handleAddMedicalRecord}
                    className="px-3 py-1 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    + Add
                  </button>
                </div>
                {!detail.medicalRecords ||
                detail.medicalRecords.length === 0 ? (
                  <EmptyState msg="No medical records yet." />
                ) : (
                  <ul className="divide-y">
                    {detail.medicalRecords.map((rec) => (
                      <li
                        key={rec.recordId ?? rec.id}
                        className="py-3 flex items-start justify-between gap-3"
                      >
                        <div className="text-sm">
                          <div className="font-medium">
                            {rec.summary || rec.recordType || "Record"}
                          </div>
                          <div className="text-gray-600">
                            {rec.notes || "—"}
                          </div>
                          {rec.visitDate && (
                            <div className="text-gray-500 text-xs mt-1">
                              Visit: {rec.visitDate}
                            </div>
                          )}
                          {rec.createdAt && (
                            <div className="text-gray-500 text-xs">
                              Created Time: {fmtTime(rec.createdAt)}
                            </div>
                          )}
                        </div>
                        <div className="shrink-0 space-x-2">
                          <button
                            onClick={() =>
                              handleEditMedicalRecord(rec.recordId ?? rec.id)
                            }
                            className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteMedicalRecord(rec.recordId ?? rec.id)
                            }
                            className="px-3 py-1 text-sm rounded bg-red-600 text-white hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Prescriptions */}
              <section className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Prescriptions</h4>
                  <button
                    onClick={handleAddPrescription}
                    className="px-3 py-1 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    + Add
                  </button>
                </div>
                {!detail.prescriptions || detail.prescriptions.length === 0 ? (
                  <EmptyState msg="No prescriptions yet." />
                ) : (
                  <ul className="divide-y">
                    {detail.prescriptions.map((rx) => (
                      <li
                        key={rx.prescriptionId ?? rx.id}
                        className="py-3 flex items-start justify-between gap-3"
                      >
                        <div className="text-sm">
                          <div className="font-medium">
                            {rx.medicationName || "Prescription"}
                            {rx.strength ? ` • ${rx.strength}` : ""}
                            {rx.form ? ` • ${rx.form}` : ""}
                          </div>
                          <div className="text-gray-600">
                            {rx.frequency ? `Frequency: ${rx.frequency}` : "—"}
                            {rx.durationDays != null
                              ? ` • ${rx.durationDays} days`
                              : ""}
                          </div>
                          <div className="text-gray-500 text-xs mt-1">
                            {rx.startDate ? `Start: ${rx.startDate}` : ""}
                            {rx.endDate ? ` • End: ${rx.endDate}` : ""}
                            {rx.prescriptionTime
                              ? ` • Time: ${rx.prescriptionTime}`
                              : ""}
                          </div>
                          {rx.instructions && (
                            <div className="text-gray-600 text-xs mt-1">
                              Notes: {rx.instructions}
                            </div>
                          )}
                        </div>
                        <div className="shrink-0 space-x-2">
                          <button
                            onClick={() =>
                              handleEditPrescription(rx.prescriptionId ?? rx.id)
                            }
                            className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDeletePrescription(
                                rx.prescriptionId ?? rx.id,
                              )
                            }
                            className="px-3 py-1 text-sm rounded bg-red-600 text-white hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <MessagesPanel
                providerId={providerId}
                patientId={selectedPatientId}
              />
            </>
          )}
        </div>
      ) : (
        // List view — clickable cards
        <div>
          <h3 className="text-xl font-semibold mb-4">Active Patients</h3>

          {cardsLoading && (
            <div className="p-4 rounded border bg-white shadow-sm animate-pulse">
              Loading…
            </div>
          )}
          {cardsError && (
            <div className="p-4 rounded border bg-red-50 text-red-700">
              Error: {cardsError.message}
            </div>
          )}

          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {cards.map((p) => (
              <button
                key={p.patientId}
                onClick={() => setSelectedPatientId(p.patientId)}
                className="w-full text-left p-4 rounded-xl border bg-white shadow-sm hover:shadow-md transition"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-base font-semibold">
                      {p.firstName} {p.lastName}
                    </div>
                    <div className="text-sm text-gray-600">{p.email}</div>
                    <div className="text-sm text-gray-500">
                      DOB: {p.dateOfBirth}
                    </div>
                    <div className="text-sm text-gray-500">
                      Phone: {p.phoneNumber || "—"}
                    </div>
                  </div>

                  {/* optional right-side meta */}
                  {p.totalAppointments != null && (
                    <div className="shrink-0 text-right">
                      <div className="text-xs uppercase text-gray-500">
                        Visits
                      </div>
                      <div className="text-sm font-medium">
                        {p.totalAppointments}
                      </div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Pagination stays the same */}
          {cardsPage.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {cardsPage.number + 1} of {cardsPage.totalPages}
              </span>
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                onClick={() =>
                  setPage((p) => Math.min(cardsPage.totalPages - 1, p + 1))
                }
                disabled={page >= cardsPage.totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modals are rendered at the root so they’re available in both views */}
      <MedicalRecordModal
        isOpen={mrOpen}
        onClose={() => setMrOpen(false)}
        mode={mrMode}
        initialData={mrInitial}
        patientId={selectedPatientId}
        providerId={providerId}
        onSaved={refreshDetail}
      />

      <PrescriptionModal
        isOpen={rxOpen}
        onClose={() => setRxOpen(false)}
        mode={rxMode}
        initialData={rxInitial}
        patientId={selectedPatientId}
        providerId={providerId}
        onSaved={refreshDetail}
      />
    </>
  );
}

/* ---------- small presentational helpers ---------- */
function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="text-gray-900">{value ?? "—"}</div>
    </div>
  );
}
function EmptyState({ msg }) {
  return (
    <div className="p-4 rounded border border-dashed text-sm text-gray-600 bg-gray-50">
      {msg}
    </div>
  );
}

function formatInsuranceDocCategory(category) {
  if (category === "INSURANCE_CARD_FRONT") return "Front";
  if (category === "INSURANCE_CARD_BACK") return "Back";
  return category || "Insurance card";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
